import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { storeApi } from "@/lib/storefront";
import {
  ArrowRight, Mail, Sparkles, Leaf, PackageCheck, MessagesSquare,
  Truck, RotateCcw, Globe, Calendar, AlertCircle, Clock, RefreshCw,
  MapPin, Phone, Quote, ChevronRight,
} from "lucide-react";
import SEO from "@/components/storefront/SEO";
import JsonLd, { breadcrumbSchema } from "@/components/storefront/JsonLd";

// Lookup map for block icons (default + per-item overrides)
const ICONS = {
  Sparkles, Leaf, PackageCheck, MessagesSquare, Truck, RotateCcw, Globe,
  Calendar, AlertCircle, Clock, RefreshCw, MapPin, Phone,
};

export default function ContentPage({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    storeApi.get(`/pages/${slug}`)
      .then((r) => setPage(r.data))
      .catch(() => setPage(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl px-6 py-24" data-testid={`page-${slug}-loading`}>
        <div className="h-12 w-1/2 animate-pulse rounded bg-[#E8E2D2]" />
        <div className="mt-6 space-y-3">
          {[...Array(5)].map((_, i) => <div key={i} className="h-4 w-full animate-pulse rounded bg-[#E8E2D2]" />)}
        </div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-24 text-center" data-testid="page-not-found">
        <div className="text-xs uppercase tracking-[0.3em] text-[#2D2823]">404</div>
        <h1 className="mt-3 font-display text-5xl">Page not found</h1>
        <Link to="/" className="mt-6 inline-block text-[#7D8471] underline decoration-[#7D8471] underline-offset-4 hover:text-[#1A1A1A]">Back to shop</Link>
      </div>
    );
  }

  const isAbout = slug === "about";
  const blocks = Array.isArray(page.blocks) ? page.blocks : [];
  // Backwards-compat: if page only has legacy `sections`, render them as section-blocks
  const renderBlocks = blocks.length > 0
    ? blocks
    : (Array.isArray(page.sections) ? page.sections.map((s) => ({ type: "section", ...s })) : []);

  return (
    <article data-testid={`page-${slug}`}>
      <SEO title={page.title} description={page.intro || page.subtitle || page.body?.slice(0, 160)} />
      <JsonLd data={breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: page.title, url: typeof window !== "undefined" ? window.location.pathname : undefined },
      ])} />

      {/* Hero */}
      {isAbout ? <AboutHero page={page} /> : <CompactHero page={page} slug={slug} />}

      {/* Body — render every block in order */}
      <section className="mx-auto max-w-[1280px] px-6 py-16 md:px-10 md:py-24">
        <div className="space-y-20">
          {renderBlocks.map((b, i) => <Block key={blockKey(b, i)} block={b} index={i} />)}

          {page.closing && (
            <div className="border-t border-[#DDD6C5] pt-14 text-center">
              <p className="font-display text-2xl italic text-[#1A1A1A] md:text-3xl">{page.closing}</p>
            </div>
          )}

          {/* Cross-links */}
          <CrossLinks />
        </div>
      </section>
    </article>
  );
}

// ---------- Heroes ----------
function AboutHero({ page }) {
  return (
    <section className="relative overflow-hidden bg-[#1A1A1A] text-white">
      {page.hero_image && (
        <>
          <img src={page.hero_image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/70" />
        </>
      )}
      <div className="relative mx-auto flex min-h-[68vh] max-w-[1280px] flex-col justify-end px-6 pb-16 pt-32 md:px-10 md:pb-24 md:pt-44">
        <div className="mb-3 inline-flex items-center gap-2 self-start rounded-full border border-white/30 bg-white/5 px-3 py-1 text-[10px] uppercase tracking-[0.35em] text-white/85 backdrop-blur">
          <span className="h-1.5 w-1.5 rounded-full bg-[#C2B280]" />
          {page.subtitle}
        </div>
        <h1 className="max-w-4xl font-display text-5xl leading-[1.02] tracking-tight md:text-[88px]" data-testid="page-about-title">{page.title}</h1>
        {page.intro && <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/85 md:text-lg">{page.intro}</p>}
      </div>
    </section>
  );
}

function CompactHero({ page, slug }) {
  return (
    <section className="border-b border-[#DDD6C5] bg-[#F2EDDF]">
      <div className="mx-auto max-w-3xl px-6 py-16 md:py-24">
        {page.subtitle && <div className="mb-3 text-xs uppercase tracking-[0.3em] text-[#2D2823]" data-testid={`page-${slug}-subtitle`}>{page.subtitle}</div>}
        <h1 className="font-display text-4xl leading-tight md:text-[64px]" data-testid={`page-${slug}-title`}>{page.title}</h1>
        {page.intro && <p className="mt-5 text-lg leading-relaxed text-[#3D3833] md:text-xl">{page.intro}</p>}
        {page.updated_at && <div className="mt-4 text-xs text-[#2D2823]">{page.updated_at}</div>}
      </div>
    </section>
  );
}

// ---------- Block dispatcher ----------
function Block({ block }) {
  switch (block.type) {
    case "stats": return <StatsBlock items={block.items} />;
    case "split": return <SplitBlock {...block} />;
    case "icon-grid": return <IconGridBlock {...block} />;
    case "process": return <ProcessBlock {...block} />;
    case "table": return <TableBlock {...block} />;
    case "highlight": return <HighlightBlock {...block} />;
    case "image": return <ImageBlock {...block} />;
    case "quote": return <QuoteBlock {...block} />;
    case "contact-grid": return <ContactGridBlock items={block.items} />;
    case "address": return <AddressBlock {...block} />;
    case "toc": return <TocBlock items={block.items} />;
    case "section": return <SectionBlock {...block} />;
    default: return null;
  }
}

// ---------- Blocks ----------
function StatsBlock({ items }) {
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-8 border-y border-[#DDD6C5] py-10 md:grid-cols-4 md:py-14" data-testid="block-stats">
      {items.map((s) => (
        <div key={`${s.k}-${s.d}`} className="text-center md:text-left">
          <div className="font-display store-nums text-3xl tracking-tight text-[#1A1A1A] md:text-4xl">{s.k}</div>
          <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-[#2D2823] md:text-xs">{s.d}</div>
        </div>
      ))}
    </div>
  );
}

function SplitBlock({ image, image_left, eyebrow, heading, body }) {
  return (
    <div className={`grid items-center gap-10 md:grid-cols-2 md:gap-16 ${image_left ? "" : "md:[&>div:first-child]:order-2"}`} data-testid="block-split">
      <div className="relative aspect-[5/6] overflow-hidden rounded-md bg-[#E8E2D2] md:aspect-[4/5]">
        {image && <img src={image} alt="" className="h-full w-full object-cover" />}
      </div>
      <div className="max-w-md">
        {eyebrow && <div className="mb-3 text-[10px] uppercase tracking-[0.3em] text-[#2D2823]">{eyebrow}</div>}
        <h2 className="font-display text-3xl leading-tight text-[#1A1A1A] md:text-[44px]">{prettyHeading(heading)}</h2>
        {body && (
          <div className="mt-5 space-y-4 text-base leading-relaxed text-[#3D3833] md:text-lg">
            {body.split(/\n\s*\n/).map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function IconGridBlock({ eyebrow, heading, items }) {
  return (
    <div data-testid="block-icon-grid">
      {(eyebrow || heading) && (
        <div className="mb-10 max-w-2xl">
          {eyebrow && <div className="mb-3 text-[10px] uppercase tracking-[0.3em] text-[#2D2823]">{eyebrow}</div>}
          {heading && <h2 className="font-display text-3xl leading-tight text-[#1A1A1A] md:text-[44px]">{prettyHeading(heading)}</h2>}
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        {items.map((it) => {
          const Icon = ICONS[it.icon] || Sparkles;
          const slug = slugify(it.label);
          return (
            <div key={slug} className="rounded-md border border-[#DDD6C5] bg-white p-6 transition-shadow hover:shadow-[0_8px_30px_-12px_rgba(0,0,0,0.12)]" data-testid={`icon-grid-item-${slug}`}>
              <Icon size={20} strokeWidth={1.4} className="text-[#7D8471]" />
              <div className="mt-3 font-display text-lg text-[#1A1A1A]">{it.label}</div>
              <p className="mt-1 text-sm leading-relaxed text-[#2D2823]">{it.body}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ProcessBlock({ eyebrow, heading, items }) {
  return (
    <div data-testid="block-process">
      {(eyebrow || heading) && (
        <div className="mb-10 max-w-2xl">
          {eyebrow && <div className="mb-3 text-[10px] uppercase tracking-[0.3em] text-[#2D2823]">{eyebrow}</div>}
          {heading && <h2 className="font-display text-3xl leading-tight text-[#1A1A1A] md:text-[44px]">{prettyHeading(heading)}</h2>}
        </div>
      )}
      <ol className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {items.map((it, i) => (
          <li key={`${it.n}-${slugify(it.label)}`} className="relative rounded-md border border-[#DDD6C5] bg-white p-6">
            <span className="font-display store-nums text-5xl text-[#E8E2D2]" aria-hidden="true">{it.n}</span>
            <div className="mt-2 font-display text-lg text-[#1A1A1A]">{it.label}</div>
            <p className="mt-2 text-sm leading-relaxed text-[#2D2823]">{it.body}</p>
            {i < items.length - 1 && (
              <div className="hidden lg:absolute lg:right-[-22px] lg:top-1/2 lg:flex lg:-translate-y-1/2 lg:items-center lg:text-[#C2B280]">
                <ChevronRight size={20} strokeWidth={1.4} />
              </div>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

function TableBlock({ eyebrow, heading, headers, rows }) {
  return (
    <div data-testid="block-table">
      {(eyebrow || heading) && (
        <div className="mb-8 max-w-2xl">
          {eyebrow && <div className="mb-3 text-[10px] uppercase tracking-[0.3em] text-[#2D2823]">{eyebrow}</div>}
          {heading && <h2 className="font-display text-3xl leading-tight text-[#1A1A1A] md:text-[40px]">{prettyHeading(heading)}</h2>}
        </div>
      )}
      <div className="overflow-hidden rounded-md border border-[#DDD6C5] bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F2EDDF] text-[10px] uppercase tracking-[0.18em] text-[#2D2823]">
              {headers.map((h) => <th key={h} className="px-5 py-3 text-left font-medium">{h}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#DDD6C5]">
            {rows.map((row) => (
              <tr key={row.join("|")} className="hover:bg-[#F2EDDF]">
                {row.map((cell, j) => (
                  <td key={`${j}-${cell}`} className={`px-5 py-3.5 ${j === 0 ? "font-medium text-[#1A1A1A]" : "text-[#3D3833]"}`}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function HighlightBlock({ icon, label, body, link }) {
  const Icon = ICONS[icon] || Sparkles;
  return (
    <div className="flex items-start gap-5 rounded-md border border-[#C2B280]/40 bg-gradient-to-br from-[#E8E2D2] to-[#F2EDDF] p-6 md:p-8" data-testid="block-highlight">
      <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-[#1A1A1A] text-white">
        <Icon size={20} strokeWidth={1.4} />
      </div>
      <div className="min-w-0 flex-1">
        {label && <div className="font-display text-xl text-[#1A1A1A]">{label}</div>}
        <p className="mt-1 text-sm leading-relaxed text-[#3D3833] md:text-base">{body}</p>
        {link && (
          <a href={link.href} className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#7D8471] hover:text-[#1A1A1A]" target={link.href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer">
            {link.label} <ArrowRight size={12} />
          </a>
        )}
      </div>
    </div>
  );
}

function ImageBlock({ src, caption }) {
  return (
    <figure className="space-y-3" data-testid="block-image">
      <div className="aspect-[16/9] overflow-hidden rounded-md bg-[#E8E2D2] md:aspect-[21/9]">
        {src && <img src={src} alt={caption || ""} className="h-full w-full object-cover" />}
      </div>
      {caption && <figcaption className="text-center text-xs italic text-[#2D2823]">{caption}</figcaption>}
    </figure>
  );
}

function QuoteBlock({ text, attribution }) {
  return (
    <blockquote className="mx-auto max-w-3xl text-center" data-testid="block-quote">
      <Quote size={28} className="mx-auto text-[#C2B280]" strokeWidth={1.4} />
      <p className="mt-5 font-display text-2xl italic leading-[1.4] text-[#1A1A1A] md:text-[34px] md:leading-[1.3]">"{text}"</p>
      {attribution && <footer className="mt-4 text-xs uppercase tracking-[0.25em] text-[#2D2823]">{attribution}</footer>}
    </blockquote>
  );
}

function ContactGridBlock({ items }) {
  return (
    <div className="grid gap-5 md:grid-cols-3" data-testid="block-contact-grid">
      {items.map((it, i) => (
        <div key={i} className="rounded-md border border-[#DDD6C5] bg-white p-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#7D8471]">{it.label}</div>
          <a href={`mailto:${it.email}`} className="mt-2 block break-all font-display text-lg text-[#1A1A1A] hover:text-[#7D8471]">{it.email}</a>
          {it.hours && <div className="mt-1 text-xs text-[#2D2823]">{it.hours}</div>}
          {it.body && <p className="mt-3 text-sm leading-relaxed text-[#3D3833]">{it.body}</p>}
        </div>
      ))}
    </div>
  );
}

function AddressBlock({ lines, phone }) {
  return (
    <div className="grid gap-6 rounded-md border border-[#DDD6C5] bg-white p-8 md:grid-cols-[1fr_1fr] md:items-center" data-testid="block-address">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#7D8471]">Visit us</div>
        <address className="mt-2 not-italic">
          {lines.map((l, i) => (
            <div key={i} className={i === 0 ? "font-display text-xl text-[#1A1A1A]" : "text-sm text-[#3D3833]"}>{l}</div>
          ))}
        </address>
        {phone && (
          <div className="mt-3 inline-flex items-center gap-2 text-sm text-[#3D3833]">
            <Phone size={13} className="text-[#7D8471]" /> <a href={`tel:${phone.replace(/\s+/g, "")}`} className="hover:text-[#1A1A1A]">{phone}</a>
          </div>
        )}
      </div>
      <div className="flex h-40 items-center justify-center rounded-md bg-[#E8E2D2] text-[#7D8471] md:h-full">
        <MapPin size={32} strokeWidth={1.2} />
      </div>
    </div>
  );
}

function TocBlock({ items }) {
  return (
    <nav className="rounded-md border border-[#DDD6C5] bg-white p-6 md:p-8" aria-label="On this page" data-testid="block-toc">
      <div className="mb-4 text-[10px] uppercase tracking-[0.25em] text-[#7D8471]">On this page</div>
      <ol className="grid gap-2 md:grid-cols-2">
        {items.map((it, i) => (
          <li key={i}>
            <a href={`#${it.anchor}`} className="group flex items-center gap-2 text-sm text-[#3D3833] transition-colors hover:text-[#1A1A1A]">
              <span className="store-nums w-6 text-xs text-[#2D2823]">{String(i + 1).padStart(2, "0")}</span>
              <span className="flex-1">{it.label}</span>
              <ChevronRight size={12} className="text-[#C2B280] transition-transform group-hover:translate-x-0.5" />
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function SectionBlock({ heading, body, anchor }) {
  // Derive a stable unique id when no anchor is provided (e.g. terms numbered sections)
  const slug = anchor || (heading ? heading.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40) : null);
  return (
    <div id={anchor || undefined} className="scroll-mt-28" data-testid={`block-section-${slug || "x"}`}>
      <h2 className="mb-4 font-display text-2xl text-[#1A1A1A] md:text-3xl">{heading}</h2>
      <p className="text-[15px] leading-[1.85] text-[#3D3833] md:text-base">{body}</p>
    </div>
  );
}

// ---------- Helpers ----------
function blockKey(b, i) {
  // Stable key derived from block type + heading/anchor when available; falls back to index.
  const tag = b.anchor || b.heading || b.eyebrow || b.label || b.text;
  return `${b.type}-${tag ? slugify(tag) : i}`;
}

function slugify(s) {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 50);
}

function prettyHeading(heading) {
  if (!heading) return null;
  const words = heading.split(" ");
  if (words.length < 2) return heading;
  return (
    <>
      {words.slice(0, -1).join(" ")}{" "}
      <span className="font-display-soft italic">{words.slice(-1).join(" ")}</span>
    </>
  );
}

function CrossLinks() {
  return (
    <div className="grid gap-3 border-t border-[#DDD6C5] pt-12 text-sm md:grid-cols-3">
      <Link to="/contact" className="group flex items-center justify-between rounded-md border border-[#DDD6C5] bg-white p-5 transition-colors hover:bg-[#E8E2D2]">
        <span className="flex items-center gap-2 text-[#1A1A1A]"><Mail size={14} /> Get in touch</span>
        <ArrowRight size={14} className="text-[#7D8471] transition-transform group-hover:translate-x-1" />
      </Link>
      <Link to="/faq" className="group flex items-center justify-between rounded-md border border-[#DDD6C5] bg-white p-5 transition-colors hover:bg-[#E8E2D2]">
        <span className="flex items-center gap-2 text-[#1A1A1A]"><MessagesSquare size={14} /> Browse the FAQ</span>
        <ArrowRight size={14} className="text-[#7D8471] transition-transform group-hover:translate-x-1" />
      </Link>
      <Link to="/catalog" className="group flex items-center justify-between rounded-md border border-[#DDD6C5] bg-white p-5 transition-colors hover:bg-[#E8E2D2]">
        <span className="text-[#1A1A1A]">Shop the edit</span>
        <ArrowRight size={14} className="text-[#7D8471] transition-transform group-hover:translate-x-1" />
      </Link>
    </div>
  );
}
